import { useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { TextButtonWithIcon } from '@/components';
import { generateNumericId, IngredientWithAmount, useProduct } from '@/core';
import { EditableIngredient } from '@/features';

export const FormulaTable = () => {
  const { t } = useTranslation();
  const [ingredientsWithAmount, setIngredientsWithAmount] = useState<IngredientWithAmount[]>([]);
  const initialized = useRef(false);

  const {
    queries: { useProductIngredients },
  } = useProduct();
  const { id } = useParams<{ id: string }>();
  const { data: ingredientsResponse, isLoading, isError } = useProductIngredients(Number(id), true);

  useEffect(() => {
    if (ingredientsResponse?.data && !initialized.current) {
      setIngredientsWithAmount(ingredientsResponse.data);
      initialized.current = true;
    }
  }, [ingredientsResponse?.data]);

  useEffect(() => {
    if (ingredientsResponse?.data && initialized.current) {
      // Only sync when API data length decreases (deletions)
      if (ingredientsResponse.data.length < ingredientsWithAmount.length) {
        setIngredientsWithAmount(ingredientsResponse.data);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingredientsResponse?.data?.length]);

  const handleAddIngredientClick = () => {
    const newIngredient: IngredientWithAmount = {
      ingredient: {
        id: generateNumericId(),
        name: '',
        unit: '',
        description: '',
        price: '0',
        is_active: false,
      },
      amount: 1,
    };
    setIngredientsWithAmount([...ingredientsWithAmount, newIngredient]);
  };

  if (isLoading) return <CircularProgress />;
  if (isError || ingredientsResponse?.status !== 200)
    return <Box sx={{ fontStyle: 'italic' }}>{t('INGREDIENT.ERROR_WHILE_FETCHING')}</Box>;

  return (
    <Table size="medium" sx={{ mt: 2, width: '100%' }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ width: '30%' }}>{t('INGREDIENT.INGREDIENTS')}</TableCell>
          <TableCell sx={{ width: '15%', minWidth: '250px' }}>
            {t('INGREDIENT.GROSS_WEIGHT')}
          </TableCell>
          <TableCell sx={{ width: '1%' }}></TableCell>
          <TableCell></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {ingredientsWithAmount.map((ingredientWithAmount) => (
          <EditableIngredient
            key={ingredientWithAmount.ingredient.id}
            ingredientWithAmount={ingredientWithAmount}
            setIngredientsWithAmount={setIngredientsWithAmount}
          />
        ))}
        <TableRow>
          <TableCell>
            <TextButtonWithIcon
              iconName={'add'}
              label={t('INGREDIENT.ADD')}
              onClick={handleAddIngredientClick}
            />
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
};
